import { useClientPortalAPI } from "@certn/api";
import { Text, Picker, GhostIconButton } from "@certn/cascadia-react";
import { SkeletonGrid } from "@certn/cascadia-react/patterns/skeleton-grid/SkeletonGrid";
import { css, cx } from "@emotion/css";
import { PropsWithChildren, useEffect, useRef, useState } from "react";

import { CaseCounts } from "../../types";
export interface DashboardSummaryCardProps extends PropsWithChildren {
  className?: string;
  testID?: string;
  isLoading?: boolean;
  minHeight?: number;
  onDataChange?: (data: {
    caseCounts: CaseCounts | null;
    customerGroupId: string | null;
  }) => void;
}

export function DashboardSummaryCard(props: DashboardSummaryCardProps) {
  const {
    children,
    className,
    testID = "dashboard-summary-card",
    onDataChange,
    isLoading,
  } = props;

  const triggerRef = useRef<HTMLDivElement | null>(null);
  const [customerGroupId, setCustomerGroupId] = useState<string | null>(null);

  const { data: results = [], isLoading: isLoadingGroups } = useClientPortalAPI(
    `/api/internal/client-portal/customer-accounts/customer-groups/`
  );

  const { data: caseCounts, isLoading: isLoadingCounts } = useClientPortalAPI(
    customerGroupId
      ? `/api/internal/client-portal/case/counts/filtered/${customerGroupId}/`
      : "/api/internal/client-portal/case/counts/"
  );

  useEffect(() => {
    if (caseCounts && customerGroupId && onDataChange) {
      onDataChange({ caseCounts, customerGroupId });
    }
  }, [caseCounts, customerGroupId, onDataChange]);

  useEffect(() => {
    setCustomerGroupId(results.length > 0 ? results[0].id : null);
  }, [results]);

  return (
    <section
      aria-label={`${testID}-container`}
      data-testid={testID}
      className={cx(styles.container, className)}
      style={{
        minHeight: props.minHeight,
      }}
    >
      {isLoading || isLoadingGroups ? (
        <SkeletonGrid rows={3} columns={3} />
      ) : (
        customerGroupId && (
          <div className={styles.pickerContainer}>
            <Picker
              triggerRef={triggerRef as React.RefObject<HTMLDivElement>}
              label="Select a group"
              placement="bottom right"
              items={results}
              selectionMode="single"
              testID="dashboard-group-picker"
              selectedKeys={
                customerGroupId ? new Set([customerGroupId]) : new Set()
              }
              onSelectionChange={(selectedKeys) => {
                const selectedKey = Array.from(selectedKeys).pop();
                if (
                  selectedKey !== undefined &&
                  selectedKey !== customerGroupId
                ) {
                  setCustomerGroupId(selectedKey as string);
                }
              }}
              trigger={({ selectedItems }) => (
                <div ref={triggerRef} className={styles.triggerContainer}>
                  <Text style="global/heading-large" truncate>
                    {selectedItems[0]?.name}
                  </Text>
                  <div className={styles.triggerButton}>
                    <GhostIconButton icon="expand_more" size="small" />
                  </div>
                </div>
              )}
            />
          </div>
        )
      )}
      {!isLoading && !isLoadingCounts && children}
    </section>
  );
}

const styles = {
  container: css({
    borderWidth: "1px",
    borderStyle: "solid",
    borderRadius: "var(--border-radius-3)",
    display: "flex",
    gap: "var(--space-3)",
    flexDirection: "column",
    padding: "var(--space-5)",
    borderColor: "var(--border-base)",
    backgroundColor: "var(--surface-base)",
    position: "relative",
  }),
  titleWithTrailing: css({
    justifyContent: "space-between",
    display: "flex",
    flexDirection: "row",
  }),
  pickerContainer: css({
    display: "flex",
    justifyContent: "flex-start",
    width: "100%",
  }),
  triggerButton: css({
    padding: "var(--space-1-5)",
  }),
  triggerContainer: css({
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
  }),
};
